import dropRight from 'lodash/dropRight';
import get from 'lodash/get';
import last from 'lodash/last';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
// eslint-disable-next-line jira/import-whitelist
import { prefetchApiReadyMark } from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import { createResourceError } from '@atlassian/jira-router-resources-utils/src/utils/create-resource-error/index.tsx';
import { getProjectKeyId } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';

import { checkAuthentication } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';

import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import type { RouterDataContext, ResourceStoreContext } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { isHttpClientErrorResponse } from '@atlassian/jira-fetch';
import type { CategorizedQueueData, CategorizedQueueRestData } from '../../types.tsx';
import { transformRestData } from '../../utils/index.tsx';

const MAX_ATTEMPTS = 3;

function getMaxAttempts() {
	return __SERVER__ ? 1 : MAX_ATTEMPTS;
}

class RetriedError extends Error {
	previousErrors: Error[];

	constructor(retryErrors: Error[] = []) {
		// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
		super(last(retryErrors).message);
		this.previousErrors = dropRight(retryErrors);
	}
}

const getCategories = async (
	projectKey: string,
	failedAttempts: Error[] = [],
): Promise<CategorizedQueueData | null> => {
	try {
		const data: CategorizedQueueRestData = await fetchJson(
			`/rest/servicedesk/1/servicedesk/${projectKey}/queues/categories?operation=fetchQueueCategories`,
		);

		setMark(prefetchApiReadyMark);
		return transformRestData(data, projectKey);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('disable_queues_categories_resource_error_in_ssr') &&
			__SERVER__ &&
			isHttpClientErrorResponse(error)
		) {
			fireErrorAnalytics({
				meta: {
					id: 'jiraServicedeskQueuesNavigationItemsResource.ssrClientError',
					packageName: 'jiraRouterResourcesServiceDeskQueues',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});

			return null;
		}

		logger.safeErrorWithoutCustomerData(
			'jiraServicedeskQueuesNavigationItemsResource',
			error.message,
			{
				statusCode: error.statusCode,
				retryCount: failedAttempts.length,
			},
		);

		fireErrorAnalytics({
			meta: {
				id: 'jiraServicedeskQueuesNavigationItemsResource',
				packageName: 'jiraSpa',
			},
			error,
			attributes: {
				retryCount: failedAttempts.length,
			},
		});

		failedAttempts.push(error);

		if (failedAttempts.length >= getMaxAttempts() || !(error.statusCode >= 500)) {
			throw new RetriedError(failedAttempts);
		}

		return getCategories(projectKey, failedAttempts);
	}
};

const getData = ({ match, query }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
	// Early exit on these cases and return a valid response. We don't want to flood sentry.
	if (!checkAuthentication(tenantContext)) {
		throw createResourceError({
			component: 'spa.view.routes.servicedesk.queues.getData',
			message: 'Current user is not authenticated',
			name: 'AuthenticationError',
			skipSentry: true,
		});
	}

	// Resource is shared with non-JSM or non-JCS projects, so invoke initial RRR state on missing JSM or JCS license
	if (fg('jcs_project_type_m3')) {
		if (
			!checkLicense(tenantContext, 'servicedesk') &&
			!checkLicense(tenantContext, 'customerservice')
		) {
			return null;
		}
	} else if (!checkLicense(tenantContext, 'servicedesk')) {
		return null;
	}

	// projectKey from url path/query or browse/{issueKey}
	const projectKey =
		getProjectKeyId(match, query).projectKey || get(match.params, 'issueKey', '')?.split('-')[0];

	if (projectKey == null) {
		return null;
	}

	return getCategories(projectKey);
};

export default getData;
